/**
 * Authoring document model for Daedalus (Playbook 5.2). Everything here is plain, readonly, JSON-serialisable
 * data with typed arrays for grids; there are no class instances and no hidden state. Authoring data stores
 * intent (corner masks, placements); compiled data stores results.
 */

/**
 * Version of the serialised document format. Bumped whenever a saved document would no longer load.
 */
export const SCHEMA_VERSION = 13;

/**
 * Collision cells per tile along each axis (ADR-040): collision layers and compiled grids are at quarter-tile
 * resolution (8 px cells). Schema 1 documents stored one cell per tile and schema 2 two per tile; both are
 * expanded on load.
 */
export const COLLISION_CELLS_PER_TILE = 4;

/**
 * Collision cells per tile along each axis in the compiled grid (ADR-113 decision 2): 4 px cells, a whole
 * multiple of the authoring cell. The compile builds the rules, the shore, the opened cells and the painted
 * layers at `COLLISION_CELLS_PER_TILE`, doubles them to this, then stamps footprints and passages at it.
 * This is the one knob: the grid a chunk carries, the map server's grids and the prediction follow it.
 */
export const COMPILED_COLLISION_CELLS_PER_TILE = 8;

/**
 * Checks that the compiled cell count is a whole multiple of the authoring one, as ADR-113 decision 2 requires, so
 * the compile can double its authoring grid onto it.
 *
 * @param compiled Compiled cells per tile.
 * @param authoring Authoring cells per tile.
 * @throws Error when `compiled` is not a whole multiple of `authoring`.
 */
export function assertWholeMultiple(compiled: number, authoring: number): void {
  if (!Number.isInteger(compiled / authoring)) {
    throw new Error(`COMPILED_COLLISION_CELLS_PER_TILE ${compiled} is not a whole multiple of COLLISION_CELLS_PER_TILE ${authoring}`);
  }
}

assertWholeMultiple(COMPILED_COLLISION_CELLS_PER_TILE, COLLISION_CELLS_PER_TILE);

/**
 * Any JSON-representable value.
 */
export type JsonValue = string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };

/**
 * Reference to one tile of one tileset by local tile ID. This is the only tile reference form used in public
 * APIs, rules, the resolver, editor commands and the adapter. `flipX` and `flipY` mirror a hand-painted tile
 * (ADR-089); absent reads as false, and terrain and rule output never sets them.
 */
export interface TileRef {
  readonly tileset: string;
  readonly tile: number;
  readonly flipX?: boolean;
  readonly flipY?: boolean;
}

/**
 * A tileset the world uses. Its position in `WorldDocument.tilesets` is the index used by the packed tile form.
 * `frames` absent is the whole tileset. Present, the entry is partial (ADR-129 decision 3.1): only these local
 * tile ids, ascending, distinct and at least one, are what the editor offers and packs of the tileset. It never
 * bounds a reference: a prop's frame, a state frame, a transition, a tile cell or a terrain must name a listed
 * tileset, whole or partial, and nothing checks one against `frames`.
 */
export interface TilesetRef {
  readonly id: string;
  readonly frames?: readonly number[];
}

/**
 * Identifier of a terrain definition (Playbook 5.3).
 */
export type TerrainRef = string;

/**
 * Terrain painted as a corner mask. `corners` is a `(width + 1) * (height + 1)` vertex grid of wang colour
 * indices, 0 meaning none; tiles are derived from it at compile time. `autoName` true means the editor names
 * the layer after the entry it paints and keeps the name so; false means the author typed the name; absent,
 * the editor decides when the world opens (ADR-038 amendment of 2026-09-26, decision 5). `faceHeight` is the
 * number of rows the face below a painted top takes, lip and foot included, for a terrain that declares a face
 * (ADR-072); absent means the face's own height.
 */
export interface TerrainLayer {
  readonly kind: "terrain";
  readonly id: string;
  readonly name: string;
  readonly autoName?: boolean;
  readonly group?: string;
  readonly terrain: TerrainRef;
  readonly variant: string;
  readonly faceHeight?: number;
  readonly corners: Readonly<Uint8Array>;
}

/**
 * Manually placed tiles. Each entry is a packed `u32` (ADR-089): bit 31 mirrors the tile horizontally, bit 30
 * vertically, bit 29 is reserved, bits 16 to 28 are the tileset index and bits 0 to 15 the local tile id, with
 * `EMPTY_TILE` for empty cells; see `packed-tile.ts` for conversion to and from `TileRef`.
 */
export interface TileLayer {
  readonly kind: "tile";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly tiles: Readonly<Uint32Array>;
}

/**
 * A prop placed on the grid: one frame from a prop atlas at a tile position.
 */
/**
 * The behaviour a prop carries (ADR-053): the object type's id, the author's field values, the frame each
 * state past the initial one shows, and the cells a player must stand in to use it. The prop's own frame is
 * the initial state's frame, so any prop can be made interactive and keeps the art the author chose.
 * `frames` is keyed by state id; `activation` holds `[column, rowsUp]` cells in the frame's own space, where
 * a cell may lie outside the frame so activation cells can reach in front of the art. `animations` is keyed by the
 * animation name a type's transition `plays` (ADR-058) and names the first frame of the one-shot strip that
 * transition shows; a transition whose name has no entry cuts straight to the new state's frame.
 */
export interface PropBehaviour {
  readonly type: string;
  readonly fields: { readonly [key: string]: JsonValue };
  readonly frames?: { readonly [stateId: string]: TileRef };
  readonly animations?: { readonly [name: string]: TileRef };
  readonly activation?: readonly (readonly [number, number])[];
}

/**
 * A placed prop's own footprint (ADR-040 amendment of 2026-10-04): the collision cells this one prop blocks,
 * `[column, rowsUp]` from its frame's bottom-left as the library's per-frame override counts them, in place of
 * the footprint its frame shares with every other copy. The cells are held in canonical order (by rows up,
 * then column, each once); an empty list blocks nothing. `behind` and `front` (ADR-132 decision 12) are the one
 * prop's own Behind and In front cells, in the same cells and bounds as a frame's marks (marks may lie up to
 * `STAND_REACH` cells below zero), in the same canonical order, no cell in two of the three lists. Each of the two
 * is decided on its own: a list left out inherits the frame's list of that mark, so a footprint of blocked cells
 * alone keeps the frame's colours; an empty list means the prop has none of that mark; a list present replaces the
 * frame's for this one prop.
 */
export interface InstanceFootprint {
  readonly cells: readonly (readonly [number, number])[];
  readonly behind?: readonly (readonly [number, number])[];
  readonly front?: readonly (readonly [number, number])[];
}

/**
 * A placed prop. `sortPixels` moves the line it sorts by against characters and other props, in whole world
 * pixels, positive drawing it in front (ADR-098 decision 2); one row is the world's tile size.
 */
export interface PropInstance {
  readonly id: string;
  readonly frame: TileRef;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly sortPixels: number;
  readonly behaviour?: PropBehaviour;
  /** Absent: the prop blocks by its frame's footprint. Never present beside a behaviour. */
  readonly footprint?: InstanceFootprint;
  readonly keepPlacement?: true;
}

/**
 * Free-standing props layered over the terrain.
 */
export interface PropLayer {
  readonly kind: "prop";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly props: readonly PropInstance[];
}

/**
 * A game entity placed by the editor. `type` names an `EntityTypeDefinition` registered by the game adapter,
 * and `fields` holds the values for that definition's schema.
 */
export interface EntityInstance {
  readonly id: string;
  readonly type: string;
  readonly x: number;
  readonly y: number;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * Entities placed on the map.
 */
export interface EntityLayer {
  readonly kind: "entity";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly entities: readonly EntityInstance[];
}

/**
 * Any layer. Order in `MapDocument.layers` is render order, bottom first.
 */
/**
 * A layer of collision bytes painted by hand (ADR-028): one byte per quarter cell (`COLLISION_CELLS_PER_TILE`
 * squared per tile, ADR-040) that ORs into the map's collision grid at compile time. Never rendered; absent
 * from chunks.
 */
export interface CollisionLayer {
  readonly kind: "collision";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly cells: Readonly<Uint8Array>;
}

/**
 * A layer of painted zones (ADR-054's amendment of 2026-09-27): one byte per collision cell, the grid of a
 * collision layer, 0 for an unpainted cell or `n` for the zone type `types[n - 1]`. `types` is the layer's own
 * table of the type ids the game registers; the document never stores a type's place in the registration. A
 * layer is kept canonical (`canonicalZoneLayer`): its table lists exactly the ids its cells use, in the order
 * a row-major scan first meets them, so equal paintings are equal documents. Never rendered by the game and
 * absent from client chunks; where two zone layers paint a cell, the one later in `MapDocument.layers` wins.
 */
export interface ZoneLayer {
  readonly kind: "zone";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly types: readonly string[];
  readonly cells: Readonly<Uint8Array>;
}

/**
 * Where a door leads (ADR-122 decision 2.1): a map by id and the id of an arrival point on it, never its name.
 */
export interface DoorTarget {
  readonly map: string;
  readonly arrival: string;
}

/**
 * One door of a door layer (ADR-122 decision 2.1): a stable id never shown, the builder's name, where it leads,
 * and the values of the game's door fields (`DaedalusGameAdapter.doorFields`), as an entity's `fields` holds its
 * type's. Daedalus edits, validates and compiles the fields without reading their meaning.
 */
export interface DoorDefinition {
  readonly id: string;
  readonly name: string;
  readonly to: DoorTarget;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * A layer of painted doors (ADR-122 decision 2.1): one byte per collision cell, the grid of a zone layer, 0 for no
 * door or `n` for `doors[n - 1]`. `doors` is the builder's list in the builder's order, a door with no cell kept,
 * at most `MAX_DOORS_PER_LAYER`; every edit names a door by its id, never its byte, so equal lists and equal
 * paintings are equal documents. A door's id is unique within its map. Never rendered by the game and absent from
 * client chunks; where two door layers paint a cell, the one later in `MapDocument.layers` wins.
 */
export interface DoorLayer {
  readonly kind: "door";
  readonly id: string;
  readonly name: string;
  readonly group?: string;
  readonly doors: readonly DoorDefinition[];
  readonly cells: Readonly<Uint8Array>;
}

export type LayerDocument = TerrainLayer | TileLayer | PropLayer | EntityLayer | CollisionLayer | ZoneLayer | DoorLayer;

/**
 * A map's ignored issues by key, the issue's kind and its target (`issueIgnoreKey`), each holding `true`
 * (ADR-070's amendment of 2026-10-05, decision 7). The record names no builder and no time: who ignored an
 * issue and when is the build server's to say, from what it recorded of the save or the operation, never a
 * field a client writes ("Who ignored it").
 */
export type IssueIgnores = { readonly [key: string]: true };

/**
 * A layer group (ADR-043): a named folder in the layers panel. Membership is the layer's `group`; draw
 * order stays the flat layer order, so groups change nothing about compiling or rendering.
 */
export interface LayerGroup {
  readonly id: string;
  readonly name: string;
}

/**
 * A map's place on the world grid (ADR-109 decision 1): the world position of its top-left corner in whole
 * tiles, each a multiple of the world's chunk size and within `PLACE_LIMIT` either side of the origin, and its
 * depth (ADR-122 decision 1.2), a whole number from -8 below the surface to 0, present only when below it.
 */
export interface MapPlace {
  readonly x: number;
  readonly y: number;
  readonly depth?: number;
}

/**
 * A room's host (ADR-122 decision 1.2): the placed map its front door stands on and the id of the arrival
 * point outside it. Everything that reads a room's world position reads this point's.
 */
export interface MapHost {
  readonly map: string;
  readonly arrival: string;
}

/**
 * Who shares a copy of a map (ADR-122 decision 1.3): everyone on the channel, a party, one character or an
 * owner.
 */
export type MapShare = "open" | "party" | "solo" | "owner";

/**
 * Whether a map has a sky (weather, day and night) or is indoors (ADR-122 decision 6).
 */
export type MapLight = "sky" | "indoor";

/**
 * A map's atmosphere (ADR-122 decision 6): the names of its music and ambience, each one the game lists, and
 * its light; each absent when unset, an absent light meaning sky on the grid and indoor off it. `dim` is how dark an
 * indoor map is held at every hour, 0 to `MAX_MAP_DIM` (ADR-141 decision D4); absent reads 0, and a sky map ignores it.
 */
export interface MapAtmosphere {
  readonly music?: string;
  readonly ambience?: string;
  readonly light?: MapLight;
  readonly dim?: number;
}

/**
 * The grid the World view draws over the places (ADR-109 decision 3): a cell's side in tiles, a multiple of the
 * chunk size, and the world position of the cell named A1. It is a view only: no map's place is read from it.
 */
export interface WorldGrid {
  readonly cell: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A named group of maps (ADR-109 decision 7), a game's region: its id, its name and the colour the World view
 * draws its maps in, `#rrggbb`.
 */
export interface MapGroup {
  readonly id: string;
  readonly name: string;
  readonly colour: string;
}

/**
 * Whether a seam may be crossed (ADR-109 decision 6): `open`, a way through is required; `closed`, nothing
 * crosses; `closed-but`, closed but for the crossings listed.
 */
export type SeamState = "open" | "closed" | "closed-but";

/**
 * How a crossing is used: walked, swum or sailed, or walked into the neighbour where it stops (`walk-in`).
 */
export type CrossingKind = "walk" | "water" | "walk-in";

/**
 * One way across a seam, keyed by its name within the seam. `from` and `to` are inclusive corner numbers of
 * map `a`'s edge, counted from 0 at the west end of its south edge or the north end of its east edge, both
 * absent while the position is still to be designed. `fixed` marks a span that is measured or must match one
 * that is, present only when true.
 */
export interface SeamCrossing {
  readonly name: string;
  readonly kind: CrossingKind;
  readonly from?: number;
  readonly to?: number;
  readonly fixed?: true;
}

/**
 * The edge two placed maps share (ADR-109 decision 6): `a` the map to the north or the west of `b`, the pair
 * the seam's key, its state, a free word for its barrier, and its crossings in name order.
 */
export interface Seam {
  readonly a: string;
  readonly b: string;
  readonly state: SeamState;
  readonly barrier?: string;
  readonly crossings: readonly SeamCrossing[];
}

/**
 * One map of a world. `playersAbove` (ADR-047) is the number of ground layers: layers below that index draw
 * under every character, layers from it up share the Players band and sort with characters by row. Absent
 * in older saves, where it defaults to the lowest prop layer (see `playersAbove` in commands). `ignoredIssues`
 * holds the issues the builders chose to ignore (ADR-070's amendment of 2026-10-05), absent when there are
 * none; only the editor's Issues list reads it, never the compile. `place` puts the map on the world grid,
 * absent for a map that stands alone; `mapGroup` names the world's group it belongs to; `draft` marks a map not
 * yet ready, present only when true (ADR-109 decisions 1, 7 and 9). `host` makes the map a room off the grid;
 * `share` says who shares a copy, absent for everyone on the channel; `capacity` caps an open copy; and
 * `atmosphere` names its music, ambience and light (ADR-122 decisions 1.2, 1.3, 4.2 and 6).
 */
export interface MapDocument {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly seed: number;
  readonly place?: MapPlace;
  readonly host?: MapHost;
  readonly share?: Exclude<MapShare, "open">;
  readonly capacity?: number;
  readonly atmosphere?: MapAtmosphere;
  readonly mapGroup?: string;
  readonly draft?: true;
  readonly playersAbove?: number;
  readonly groups?: readonly LayerGroup[];
  readonly layers: readonly LayerDocument[];
  readonly properties: { readonly [key: string]: JsonValue };
  readonly ignoredIssues?: IssueIgnores;
}

/**
 * A world: shared tile geometry, the tilesets in use, the author's names for terrain entries keyed by
 * `<terrain id>#<colour>` (ADR-038 amendment of 2026-09-26, decision 4; absent when there are none), the maps,
 * and the layout: the grid the World view draws, the map groups and the seams in key order (ADR-109 decisions
 * 3, 6 and 7), each absent when there is none.
 */
export interface WorldDocument {
  readonly schemaVersion: number;
  readonly id: string;
  readonly name: string;
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly tilesets: readonly TilesetRef[];
  readonly terrainNames?: { readonly [entry: string]: string };
  readonly grid?: WorldGrid;
  readonly mapGroups?: readonly MapGroup[];
  readonly maps: readonly MapDocument[];
  readonly seams?: readonly Seam[];
}
